import type { EarningsLedgerEntry } from '@kilat/shared';

/**
 * Pure, React-free helpers for the agent-home "Recent activity" feed.
 *
 * Kept env-agnostic (no Firestore SDK, no React, no i18n runtime) so the
 * sort/slice + kind→label mapping is unit-testable in isolation. Only the
 * i18n KEY crosses this boundary — the home route resolves it via
 * `useI18n()` so the copy stays translatable (no string literals leak here).
 *
 * MONEY: these helpers never divide sen — they only read the SIGN of the
 * signed integer amount. The single divide-by-100 to reach RM stays at the
 * render boundary via `money.format`, same as everywhere else.
 */

/** The four ledger kinds an agent can see, from `EarningsLedgerEntry`. */
export type LedgerKind = EarningsLedgerEntry['kind'];

/** i18n key (under `agent.home.activity.*`) for each ledger kind's human label. */
const KIND_LABEL_KEY: Record<LedgerKind, string> = {
  accrued: 'agent.home.activity.accrued',
  adjustment: 'agent.home.activity.adjustment',
  clawed_back: 'agent.home.activity.clawedBack',
  paid: 'agent.home.activity.paid',
};

export interface LedgerEntryDescriptor {
  /** The entry's kind, echoed for callers that switch on it. */
  readonly kind: LedgerKind;
  /** i18n key resolving to the kind's human label. */
  readonly labelKey: string;
  /** Sign of the signed sen amount: 1 credit, -1 debit/reversal, 0 zero. */
  readonly sign: -1 | 0 | 1;
}

/**
 * Map a ledger entry to its display descriptor: the i18n key for its kind
 * label + the sign of its amount (drives the tasteful +/- colour). Pure —
 * no formatting, no locale, no money divide.
 */
export function describeLedgerEntry(entry: EarningsLedgerEntry): LedgerEntryDescriptor {
  const sign: -1 | 0 | 1 = entry.amount > 0 ? 1 : entry.amount < 0 ? -1 : 0;
  return { kind: entry.kind, labelKey: KIND_LABEL_KEY[entry.kind], sign };
}

/**
 * Duck-typed Firestore-Timestamp → epoch ms for a ledger entry's `accruedAt`.
 * Accepts a real `Timestamp` (has `toMillis`) or the `{ seconds, nanoseconds }`
 * shape a cache read / test fixture can surface. Unrecognised → 0 (sorts last).
 */
export function accruedAtMs(entry: EarningsLedgerEntry): number {
  const ts = entry.accruedAt;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (Number.isFinite(ts.seconds)) {
    return ts.seconds * 1000 + Math.floor((ts.nanoseconds ?? 0) / 1_000_000);
  }
  return 0;
}

/**
 * The `limit` most-recent ledger entries, newest first (by `accruedAt`).
 * Returns a fresh array — never mutates the input. A non-positive limit or
 * empty input yields `[]`.
 */
export function recentActivity(
  entries: readonly EarningsLedgerEntry[],
  limit: number,
): EarningsLedgerEntry[] {
  if (limit <= 0) return [];
  return entries
    .slice()
    .sort((a, b) => accruedAtMs(b) - accruedAtMs(a))
    .slice(0, limit);
}
